// core
import {Component, HostListener, OnInit} from "@angular/core";
import {UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
// service
import {UtilsService} from "../../utils/utils.service";
// model
import {User} from "../models/user.model";
import {UserDetailService} from "../service/user-detail.service";
import {AppConstants} from "../../utils/constants";
import {Role} from "../../role/role.model";
import {CustomerService} from "../../customer/services/customer-service.service";
import {UserRegistrationServiceService} from "../../user-registration/service/user-registration-service.service";
import {LangChangeEvent, TranslateService} from "@ngx-translate/core";
import {take} from "rxjs/operators";
import {ErrorMessageService} from "../../core/alert-message/alert-message.service";
import {UnsavedChangesComponent} from "../../shared/unsaved-changes-component";

@Component({
  selector: 'app-customer-admin-user-details',
  templateUrl: './customer-admin-user-details.component.html',
  styleUrls: ['./customer-admin-user-details.component.scss'],
  standalone: false
})
export class CustomerAdminUserDetailsComponent implements OnInit, UnsavedChangesComponent {

  userForm: UntypedFormGroup;
  private user: User = new User();

  private phoneNumberRequired: boolean = false;

  //form status
  private currentMode: string;
  private readonly = false;

  private listOfSalutations: Array<any> = new Array<any>();
  private listOfLanguages: Array<any> = new Array<any>();
  private listOfCountries: Array<any> = new Array<any>();

  public languages;
  public countries;
  public postalCodeRequired = false;

  private homePath = AppConstants.homePage;

  public englishSalutations: any;

  private roleOptions: Array<any> = new Array<any>();

  public loading = false;

  private phoneNumberValidators = [
    Validators.pattern(this.utilsService.getPhoneNumberRegex()),
    Validators.maxLength(20),
    Validators.required
  ];

  private phoneNumberValidatorsWithoutRequired = [
    Validators.pattern(this.utilsService.getPhoneNumberRegex()),
    Validators.maxLength(20)
  ];
  private userSavedOrDeletedFormOrErrorLoading: boolean = false;


  constructor(private utilsService: UtilsService,
              private route: ActivatedRoute,
              private router: Router,
              private formBuilder: UntypedFormBuilder,
              private userDetailsService: UserDetailService,
              private customerService: CustomerService,
              private userRegistrationService: UserRegistrationServiceService,
              private _translateService: TranslateService,
              private errorMessageService: ErrorMessageService,
  ) {

    _translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.listOfLanguages = this.utilsService.initializeSelectOrMultiselect(this.languages);
      this.listOfCountries = this.utilsService.initializeSelectOrMultiselect(this.countries);
      this.listOfSalutations = this.utilsService.initializeSelectOrMultiselect(this.englishSalutations);
    });

    this.userForm = this.formBuilder.group({
      salutation: [
        {
          value: null,
          disabled: false
        }
      ],
      firstName: [
        {
          value: '',
          disabled: false
        },
        Validators.compose([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(20)
        ])
      ],
      lastName: [
        {
          value: '',
          disabled: false
        },
        Validators.compose([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(40)
        ])
      ],
      email: [
        {
          value: '',
          disabled: false
        },
        Validators.compose([
          Validators.maxLength(50),
          Validators.required,
          Validators.pattern(this.utilsService.getEmailRegexPattern())
        ])
      ],
      phone: [
        {
          value: null,
          disabled: false
        },
        this.phoneNumberValidatorsWithoutRequired
      ],
      birthDate: [
        {
          value: null,
          disabled: false
        }
      ],
      postalCode: [
        {
          value: null,
          disabled: false
        },
        Validators.compose([
          Validators.maxLength(10),
          Validators.minLength(2),
          Validators.pattern(this.utilsService.getPostalCodeRegex())
        ])
      ],
      countryCode: [
        {
          value: null,
          disabled: false
        },
        Validators.required
      ],
      language: [
        {
          value: null,
          disabled: false
        },
        Validators.required
      ],
      selectedRoles: [
        {
          value: [],
          disabled: true
        }
      ],
      active: [
        {
          value: true,
          disabled: false
        }
      ],
      confirmed: [
        {
          value: false,
          disabled: false
        }
      ],
      emailVerified: [
        {
          value: false,
          disabled: true
        }
      ],
      customerAdmin: [
        {
          value: false,
          disabled: true
        }
      ],
      crmContactId: [
        {
          value: null,
          disabled: true
        }
      ]
    });
  }

  ngOnInit() {
    this.currentMode = this.route.snapshot.queryParams['type'];

    this.userRegistrationService.getSalutations().subscribe((response) => {
      this.englishSalutations = response.filter((salutation) =>
        !AppConstants.disabledSalutationCodes.includes(salutation)
      );
      this.listOfSalutations = this.utilsService.initializeListOfSalutations(this.englishSalutations);
    });

    this.userRegistrationService.getLanguages().subscribe((response) => {
      this.languages = response;
      this.listOfLanguages = this.utilsService.initializeSelectOrMultiselect(response);
    });
    this.userRegistrationService.getCountries().subscribe((response) => {
      this.countries = response;
      this.listOfCountries = this.utilsService.initializeSelectOrMultiselect(response);
    });

    this.customerService.getCurrentCustomer().subscribe((customer) => {

      this.fillRoleOptionsWithCustomerData(customer.roles);


      const targetGroup = customer.customerCategory;
      if (targetGroup === AppConstants.architectPlanner) {
        this.setupPostalCodeValidators(true);
      }
      if (targetGroup !== AppConstants.privateCustomer && targetGroup !== AppConstants.others) {
        this.phoneNumberRequired = true;
        this.userForm.controls.phone.setValidators(this.phoneNumberValidators);
        this.userForm.controls.phone.updateValueAndValidity();
      }

      if (this.isCreateMode()) {
        return;
      }

      const userId = this.route.snapshot.data['user'].id;
      this.userDetailsService.getUserById(userId).subscribe((user) => {
        this.user = user;

        this._initFormWithExistingUserData();
        this.setSelectedRoles(user.roles);

        const editableAttributes = user.editableAttributes;
        if (!editableAttributes.personal) {
          this.setupPostalCodeValidators(false);
          this.phoneNumberRequired = false;
          this.userForm.controls.phone.removeValidators(this.phoneNumberValidators);
          this.userForm.controls.phone.updateValueAndValidity();
          this.userForm.controls.salutation.disable();
          this.userForm.controls.firstName.disable();
          this.userForm.controls.lastName.disable();
          this.userForm.controls.email.disable();
          this.userForm.controls.phone.disable();
          this.userForm.controls.birthDate.disable();
          this.userForm.controls.postalCode.disable();
          this.userForm.controls.countryCode.disable();
          this.userForm.controls.language.disable();
        }

        if (!editableAttributes.accountControl) {
          this.userForm.controls.active.disable();
          this.userForm.controls.confirmed.disable();
        }
        else if (user.confirmed) {
          this.userForm.controls.confirmed.disable();
        }

        if (editableAttributes.roles && user.confirmed) {
          this.userForm.controls.selectedRoles.enable();
          this.userForm.controls.customerAdmin.enable();
        }

        if (editableAttributes.crm) {
          this.userForm.controls.crmContactId.enable();
        }

        this.readonly = !editableAttributes.personal && !editableAttributes.customer
          && !editableAttributes.accountControl && !editableAttributes.roles && !editableAttributes.sap
          && !editableAttributes.crm;

      });

    })
  }

  public getCurrentUser() {
    return this.user;
  }

  public getPhoneNumberRequired() {
    return this.phoneNumberRequired;
  }

  public isEditMode(): boolean {
    return this.currentMode === AppConstants.EDIT_MODE;
  }

  public isCreateMode(): boolean {
    return this.currentMode === AppConstants.CREATE_MODE;
  }

  public getListOfSalutations() {
    return this.listOfSalutations;
  }

  public getListOfLanguages() {
    return this.listOfLanguages;
  }

  public getListOfCountries() {
    return this.listOfCountries;
  }

  public getHomePath() {
    return this.homePath;
  }

  public getRoleOptions() {
    return this.roleOptions;
  }

  public isUserReadonly() {
    return this.readonly;
  }

  public isDirty() {
    return this.isEditMode() && this.userForm.dirty && !this.userSavedOrDeletedFormOrErrorLoading;
  }

  @HostListener('window:beforeunload')
  canUnloadWindow() {
    return !this.isEditMode() || !this.userForm.dirty;
  }

  private _initFormWithExistingUserData() {
    if (this.user.salutation && !this.englishSalutations.includes(this.user.salutation)) {
      this.englishSalutations.push(this.user.salutation);
      this.listOfSalutations = this.utilsService.initializeListOfSalutations(this.englishSalutations);
    }
    this.userForm.controls.salutation.setValue(this.user.salutation);

    this.userForm.controls.firstName.setValue(this.user.firstName);
    this.userForm.controls.lastName.setValue(this.user.lastName);
    this.userForm.controls.email.setValue(this.user.email);
    if (this.user.birthDate) {
      this.userForm.controls.birthDate.setValue(this.utilsService.convertIsoDateStringToNgbDate(this.user.birthDate));
    }
    this.userForm.controls.phone.setValue(this.user.phone);
    this.userForm.controls.language.setValue(this.user.language);
    this.userForm.controls.countryCode.setValue(this.user.countryCode);
    this.userForm.controls.postalCode.setValue(this.user.postalCode);
    this.userForm.controls.active.setValue(this.user.active);
    this.userForm.controls.confirmed.setValue(this.user.confirmed);
    this.userForm.controls.emailVerified.setValue(this.user.emailVerified);
    this.userForm.controls.customerAdmin.setValue(this.user.customerAdmin);
    this.userForm.controls.crmContactId.setValue(this.user.crmContactId);
  }

  public cancelUserCreation() {
    this.router.navigate(['/admin-users-overview']);
  }

  public save(formValues) {
    this.loading = true;
    let user: User = this.getUserFromFormValues(formValues);
    user.roles = [];
    if (formValues.selectedRoles) {
      formValues.selectedRoles.forEach((roleId) => {
        user.roles.push({id: roleId})
      });
    }
    this.userDetailsService.saveOrCreateUser(user).pipe(take(1)).subscribe({
      next: data => {
        this.loading = false;
        this.userSavedOrDeletedFormOrErrorLoading = true;
        this.router.navigate(['/admin-users-overview']);
      },
      error: (error) => {
        this.loading = false;
        // OpenErrorModal
        this.errorMessageService.openErrorModal(error);
      }
    });
  }

  private getUserFromFormValues(formValues: any): User {
    let result: User = new User();
    result.id = this.user.id;
    result.salutation = formValues.salutation;
    result.firstName = formValues.firstName;
    result.lastName = formValues.lastName;
    result.email = formValues.email;
    if (formValues.birthDate) {
      result.birthDate = this.utilsService.convertNgbDateToIsoDateString(formValues.birthDate);
    }
    result.phone = formValues.phone;
    result.language = formValues.language;
    result.countryCode = formValues.countryCode;
    result.postalCode = formValues.postalCode;
    result.active = formValues.active;
    result.confirmed = formValues.confirmed;
    result.emailVerified = formValues.emailVerified;
    result.customerAdmin = formValues.customerAdmin;
    result.crmContactId = (formValues.crmContactId && formValues.crmContactId.trim() !== '') ? formValues.crmContactId : this.user.crmContactId;
    return result;
  }

  private setupPostalCodeValidators(required) {
    this.userForm.controls.postalCode.setValidators([]);
    if (required) {
      this.postalCodeRequired = true;
      this.userForm.controls.postalCode.setValidators([
        Validators.required,
        Validators.maxLength(10),
        Validators.minLength(2),
        Validators.pattern(this.utilsService.getPostalCodeRegex())
      ]);
    } else {
      this.postalCodeRequired = false;
      this.userForm.controls.postalCode.setValidators([
        Validators.maxLength(10),
        Validators.minLength(2),
        Validators.pattern(this.utilsService.getPostalCodeRegex())
      ]);
    }
    this.userForm.controls.postalCode.markAsUntouched();
    this.userForm.controls.postalCode.updateValueAndValidity();
  }

  public deleteUser(confirmation) {
    if (confirmation) {
      this.userSavedOrDeletedFormOrErrorLoading = true;
      this.loading = true;
      this.userDetailsService.deleteUser(this.user.id).pipe(take(1)).subscribe({
        next: (result) => {
          this.loading = false;
          // show success message
          this.errorMessageService.openConfirmationModal(result);
          // navigate to manage users page
          this.router.navigate(['/admin-users-overview']);
        },
        error: (err) => {
          this.loading = false;
          this.errorMessageService.openErrorModal(err);
        }
      });
    }
  }

  private fillRoleOptionsWithCustomerData(roles: Array<any>) {
    if (roles) {
      const result: Array<Role> = new Array<Role>();
      roles.forEach(role => {
        let roleKey = role.roleKey || role.key;
        this._translateService.get(roleKey).subscribe(
          ((translatedRole) => {
            result.push(new Role(role.roleId || role.id, translatedRole));
          })
        );
      });
      result.sort((a, b) => a.label.localeCompare(b.label));
      this.roleOptions = result;
    }
  }

  private setSelectedRoles(roles: Array<any>) {
    const result: Array<string> = new Array<string>();
    if (roles) {
      roles.filter(role => this.roleOptions.find(r => r.value === role.id))
        .forEach(role => result.push(role.id || role.roleId));
    }
    this.userForm.controls.selectedRoles.setValue(result);
  }

  //set standard roles of customer in FE if user was confirmed
  public setStandardRolesOfCustomer(e) {
    if (e.target.checked) {
      this.userForm.get("selectedRoles").enable();
      this.userForm.get("customerAdmin").enable();
    } else {
      this.userForm.get("selectedRoles").disable();
      this.userForm.get("customerAdmin").disable();
      this.userForm.get("selectedRoles").reset();
      this.userForm.get("customerAdmin").reset();
    }
  }

}
